import { CheckOutlined } from "@ant-design/icons";
import { Menu } from "antd";
import Checkbox from "antd/lib/checkbox/Checkbox";
import axios from "axios";
import React, { forwardRef, useCallback, useMemo } from "react";
import { VariableSizeList } from "react-window";
import { DEBOUNCE_TIME, DROPDOWN_SIZE } from "../../../../constants/constants";
import { debounceAsync, getValueByKey } from "../../../../helper/helpers";
import "../../styles.scss";
import EmptyOption from "../emptyOption";
import ErrorOption from "../errorOption";
import LoadingOption from "../loadingOption";

const DropdownMenu = React.forwardRef((props, ref) => {
  const {
    emptyText = "Items are not available at the moment",
    data,
    itemClickHandler,
    infiniteScroll,
    loadMoreData,
    multiple,
    optionDisableProp = "disable",
    optionLabelProp = "title",
    optionValueProp = "value",
    setParentState,
    showAll,
    showCheckboxes,
    showTick = false,
    parentState,
    value,
    buttonComponent: ButtonComponent,
    isDataLoading,
  } = props;
  const {
    count,
    error,
    loading,
    pageNumber,
    searchQuery = "",
    uuid,
  } = parentState;
  const { listRef, reachedBottom, stateRef } = ref.current;

  const loadMoreDataDebounced = useCallback(
    debounceAsync(loadMoreData, DEBOUNCE_TIME, false),
    [loadMoreData]
  );

  const handleScroll = useCallback(
    async (listContainer) => {
      if (listContainer) {
        const state = stateRef.current;

        const bottom =
          Math.floor(listContainer.scrollHeight - listContainer.scrollTop) ===
          listContainer.clientHeight;
        const hasMoreData = state.count
          ? state.pageNumber <=
            Math.ceil((state.count + (showAll ? 1 : 0)) / DROPDOWN_SIZE)
          : true;

        if (
          !reachedBottom.current &&
          ((bottom && hasMoreData) || state.error)
        ) {
          reachedBottom.current = true;
          setParentState((st) => ({ ...st, loading: true, error: false }));
          loadMoreDataDebounced(state.pageNumber, state.searchQuery, true)
            .then((count) => {
              setParentState((st) => ({
                ...st,
                count,
                loading: false,
                error: false,
              }));
              reachedBottom.current = false;
              setParentState((st) => ({
                ...st,
                pageNumber: st.pageNumber + 1,
              }));
            })
            .catch((error) => {
              const requestCancelled = !axios.isCancel(error);
              setParentState((st) => ({
                ...st,
                loading: false,
                error: requestCancelled,
              }));
              reachedBottom.current = false;
            });
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [count, pageNumber, searchQuery]
  );

  const onScroll = (e) => {
    if (infiniteScroll) handleScroll(e.target);
  };

  const errorBtnClickHandler = () => {
    setParentState((st) => ({
      ...st,
      loading: true,
    }));
    loadMoreDataDebounced(pageNumber, searchQuery, true)
      .then((count) => {
        setParentState((st) => ({
          ...st,
          count,
          loading: false,
          error: false,
        }));

        reachedBottom.current = false;
      })
      .catch((error) => {
        const requestCancelled = !axios.isCancel(error);

        setParentState((st) => ({
          ...st,
          loading: false,
          error: requestCancelled,
        }));
        reachedBottom.current = false;
      });
  };

  const getItemSize = (index) => {
    const state = stateRef.current;
    if (infiniteScroll) {
      const dataLength = data?.length;
      const showAllIndex = showAll ? 0 : 1;
      const isLast =
        index === dataLength ? dataLength - showAllIndex : showAllIndex;
      if (state.error && isLast) return 120;
    }

    const isEmpty = index === 0 && data.length === 0 && !state.error;
    if (isEmpty) return 140;

    return 40;
  };

  const outerElementType = useMemo(
    () => {
      return forwardRef((props, reference) => {
        return (
          <Menu
            id={uuid}
            ref={reference}
            {...props}
            style={{
              ...(props.style || {}),
              zIndex: 99999,
              pointerEvents: "auto",
            }}
            className="custom-select-menu"
            onScroll={
              infiniteScroll
                ? (e) => {
                    props.onScroll && props.onScroll(e);
                    onScroll(e);
                  }
                : props.onScroll
            }
            onWheel={
              infiniteScroll
                ? (e) => {
                    props.onWheel && props.onWheel(e);
                    onScroll(e);
                  }
                : props.onWheel
            }
          />
        );
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <>
      {ButtonComponent && <ButtonComponent />}
      <VariableSizeList
        ref={listRef}
        height={200}
        itemSize={getItemSize}
        outerElementType={outerElementType}
        onItemsRendered={() => listRef.current?.resetAfterIndex(0)}
        itemCount={Math.max(
          1,
          data.length + (loading ? 1 : 0) + (error ? 1 : 0)
        )}
      >
        {({ style, index }) => {
          const isLast =
            index === Math.max(0, (data?.length ? data?.length : 0) - 1);

          if (loading && isLast) return <LoadingOption style={style} />;
          if (isDataLoading) return <LoadingOption style={style} />;
          else if (error && isLast)
            return (
              <ErrorOption
                errorBtnClickHandler={errorBtnClickHandler}
                style={style}
              />
            );

          const isEmpty = data.length === 0 && index === 0;
          if (isEmpty) {
            return <EmptyOption emptyText={emptyText} style={style} />;
          }

          const datum = getValueByKey(data, index);
          if (!datum) return <div />;
          const datumValue = getValueByKey(datum, optionValueProp);

          const checked = multiple
            ? value?.find(
                (item) => getValueByKey(item, optionValueProp) === datumValue
              )
            : getValueByKey(value, optionValueProp) === datumValue;

          const isOptionDisabled = getValueByKey(datum, optionDisableProp);
          const key = `${datumValue}-${index}`;
          return (
            <Menu.Item
              className={`multi-select-item ${checked ? "checked" : ""} ${
                showCheckboxes ? "with-checkbox" : ""
              } ${isOptionDisabled ? "disabled" : ""}`}
              disabled={isOptionDisabled}
              key={key}
              onMouseDown={itemClickHandler(datum, checked)}
              style={{
                ...style,
                width: "100%",
              }}
            >
              {showCheckboxes && (
                <Checkbox
                  checked={checked}
                  onChange={itemClickHandler(datum, checked)}
                />
              )}
              <span
                className="multi-select-item-title"
                title={getValueByKey(datum, optionLabelProp)}
              >
                {getValueByKey(datum, optionLabelProp)}
              </span>
              {checked && !showCheckboxes && showTick && <CheckOutlined />}
            </Menu.Item>
          );
        }}
      </VariableSizeList>
    </>
  );
});

export default DropdownMenu;
